/**
 * PATH: apps/quote/src/pages/pricelist-page/SuggestionsTab.tsx
 * PURPOSE: AI price suggestions based on historical data
 */

import { useState } from 'react';
import { Sparkles, TrendingUp, TrendingDown, Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';

interface SuggestionsTabProps {
  historicalSuggestions: any;
  suggestionsLoading: boolean;
  /** Item id currently being applied (shows a spinner on its Apply button). */
  applyingItemId?: string;
  /** Apply = set the item's sell price to the suggested value. */
  onApply: (itemId: string, suggestedPrice: number) => void;
}

export default function SuggestionsTab({ historicalSuggestions, suggestionsLoading, applyingItemId, onApply }: SuggestionsTabProps) {
  // Dismiss is client-side: historical suggestions are recomputed live from
  // quotes each load, so there is no persistent record to write — hiding the
  // row for this session is the honest, complete behaviour.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const visible = (historicalSuggestions?.suggestions ?? []).filter(
    (s: any) => !dismissed.has(s.pricebook_item_id),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-gray-500">Price update suggestions based on historical quote data</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {suggestionsLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : !visible.length ? (
          <div className="p-8 text-center text-gray-500">
            <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No price suggestions</h3>
            <p className="text-gray-500">Your pricebook appears to be aligned with historical pricing patterns.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Item</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Current</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Suggested</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Change</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Based On</th>
                <th className="px-6 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {visible.map((suggestion: any) => (
                <tr key={suggestion.pricebook_item_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 dark:text-white">{suggestion.item_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{suggestion.item_code}</div>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">{formatCurrency(suggestion.current_price)}</td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(suggestion.suggested_price)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={cn(
                      "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                      suggestion.direction === 'increase'
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    )}>
                      {suggestion.direction === 'increase'
                        ? <TrendingUp className="h-3 w-3" />
                        : <TrendingDown className="h-3 w-3" />}
                      {suggestion.discrepancy_percent.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-500">{suggestion.based_on_occurrences} quotes</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onApply(suggestion.pricebook_item_id, Number(suggestion.suggested_price))}
                        disabled={applyingItemId === suggestion.pricebook_item_id}
                        className="p-2 hover:bg-green-50 rounded-lg disabled:opacity-50"
                        title="Apply suggested price"
                      >
                        {applyingItemId === suggestion.pricebook_item_id
                          ? <Loader2 className="h-4 w-4 text-green-600 animate-spin" />
                          : <Check className="h-4 w-4 text-green-600" />}
                      </button>
                      <button
                        onClick={() => setDismissed((prev) => new Set(prev).add(suggestion.pricebook_item_id))}
                        className="p-2 hover:bg-red-50 rounded-lg"
                        title="Dismiss"
                      >
                        <X className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
